import classNames from 'classnames';
import { ScommesseEsitoSkeleton } from 'components/scommesseEsitoSkeleton';
import { ScommesseMarcatoriEsitoTipoBox } from 'components/scommesseMarcatoriEsitoTipoBox';
import { ScommesseTableTitle } from 'components/scommesseTable/scommesseTableTitle';
import { calculateBoxNumber, esito, esitoTrKey } from 'components/scommesseTipo';
import ScommesseMarcatoreEsitoOverThreeWithInfoAgg from 'components/scommesseTipo/scommesseMarcatoreEsitoOverThreeWithInfoAgg/ScommesseMarcatoreEsitoOverThreeWithInfoAgg';
import ScommesseMarcatoreWithInfoAgg from 'components/scommesseTipo/scommesseMarcatoreWithInfoAgg/ScommesseMarcatoreWithInfoAgg';
import {
  EsitiOverThreeSkeleton,
  ScommesseTipoEsitiOverThree,
} from 'components/scommesseTipo/scommesseTipoEsitiOverThree';
import { TooltipPosition } from 'components/tooltip';
import { selectIsVisualizzazioneEstesa } from 'features/settings/selectors';
import { useInfoAggiuntivaIndex, useInfoTipo, useIsAvvenimento, useScommessa } from 'features/sport/hooks';
import { KeyManagerSport } from 'features/sport/utils/keyManager';
import useWindowSize from 'hooks/useWindowSize';
import { VisualizationEnum } from 'lib/api/sport/sportScommesseBySlugResponse';
import { useTypedSelector } from 'lib/centralStore';
import React, { useMemo } from 'react';
import { calculateHeaderWidth } from 'utility/functions';
import styles from './SportMarcatoriRenderConditional.module.scss';

export type SportMarcatoriType = {
  playerKey: string;
  showMultipleBox?: boolean;
  hasTitle?: boolean;
  hasHeader?: boolean;
  isAvvenimentoTemplate?: boolean;
  isPrincipaliFilter?: boolean;
  isTooltipClicked?: boolean;
  setIsTooltipClicked?: React.Dispatch<React.SetStateAction<boolean>>;
};

export const ScommesseMarcatoriRenderConditional = ({
  playerKey,
  showMultipleBox,
  hasTitle,
  hasHeader,
  isAvvenimentoTemplate,
  isPrincipaliFilter,
  isTooltipClicked,
  setIsTooltipClicked,
}: SportMarcatoriType) => {
  const isAvvenimento = useIsAvvenimento();
  const { scommessaKey, tipoScommessaId } = new KeyManagerSport(playerKey);
  const scommessa = useScommessa(scommessaKey);

  const infoTipoScommessa = useInfoTipo(tipoScommessaId);
  const { isMobile } = useWindowSize();
  const isEstesa = useTypedSelector(selectIsVisualizzazioneEstesa);
  const playerIndex = useInfoAggiuntivaIndex(playerKey);
  const isSingleBox = useMemo(
    () => infoTipoScommessa?.tipoVisualizzazione === VisualizationEnum.EsitiUnderThree,
    [infoTipoScommessa?.tipoVisualizzazione]
  );

  const { esitoKeyList, nextInfoAgg } = playerIndex ?? {};

  const setHeaderValue = (index: number) => {
    if (!isAvvenimentoTemplate || isPrincipaliFilter) {
      return infoTipoScommessa.descrizione ?? undefined;
    }

    if (hasHeader && (isAvvenimentoTemplate || !isPrincipaliFilter)) {
      return infoTipoScommessa?.headers?.[index];
    }

    return undefined;
  };

  if (!scommessa || !playerIndex) {
    if (isSingleBox) {
      if (showMultipleBox) {
        return calculateBoxNumber(infoTipoScommessa, esitoTrKey)
          .slice(0, 2)
          .map(({ header, headerTrKey }, index) => (
            <ScommesseEsitoSkeleton
              header={hasHeader ? header : undefined}
              headerTrKey={hasHeader ? headerTrKey : undefined}
              key={`${header}_${index}`}
              className={styles.skeleton}
              isMarcatori
            />
          ));
      }

      return (
        <ScommesseEsitoSkeleton
          header={hasHeader ? esito : undefined}
          headerTrKey={hasHeader ? esitoTrKey : undefined}
          className={styles.skeleton}
          isMarcatori
        />
      );
    }
    return <EsitiOverThreeSkeleton hasHeader={hasHeader} scommessaKey={playerKey} />;
  }

  const renderComponent = () => {
    if (!infoTipoScommessa) {
      return <ScommesseEsitoSkeleton className={styles.skeleton} />;
    }

    if (nextInfoAgg) {
      if (infoTipoScommessa.numeroEsiti > 3) {
        return (
          <ScommesseMarcatoreEsitoOverThreeWithInfoAgg
            playerKey={playerKey}
            scommessaKey={scommessaKey}
            hasHeader={hasHeader}
          />
        );
      }
      return <ScommesseMarcatoreWithInfoAgg playerKey={playerKey} scommessaKey={scommessaKey} hasHeader={hasHeader} />;
    }

    if (isSingleBox) {
      if (showMultipleBox) {
        return esitoKeyList?.map((esitoKey, index) => (
          <ScommesseMarcatoriEsitoTipoBox
            keyEsito={esitoKey}
            key={esitoKey}
            header={setHeaderValue(index)}
            showHeader={isMobile || isEstesa}
          />
        ));
      }

      return (
        <ScommesseMarcatoriEsitoTipoBox
          keyEsito={esitoKeyList![0]}
          header={setHeaderValue(0)}
          showHeader={isMobile || isEstesa}
        />
      );
    }

    return (
      <div className={styles.groupWrapper}>
        <ScommesseTipoEsitiOverThree scommessaKey={scommessaKey} hasHeader={hasHeader} infoAggiuntivaKey={playerKey} />
      </div>
    );
  };

  const headerWidth = calculateHeaderWidth(
    infoTipoScommessa?.tipoVisualizzazione!,
    infoTipoScommessa?.sizeInfoAggiuntive!,
    infoTipoScommessa?.numeroEsiti!,
    infoTipoScommessa?.headers!,
    true
  );

  const renderElements = () => {
    return (
      <>
        {hasTitle && (
          <ScommesseTableTitle
            labelDx={infoTipoScommessa?.descrizione}
            labelDxTrKey={infoTipoScommessa?.descrizioneTrKey}
            tooltipText={infoTipoScommessa?.toolTip}
            tooltipTextTrKey={infoTipoScommessa?.toolTipTrKey}
            tooltipPosition={isMobile || isEstesa ? TooltipPosition.BOTTOM_LEFT : TooltipPosition.BOTTOM}
            headerWidth={headerWidth}
            isEstesa={isEstesa}
            setIsTooltipClicked={setIsTooltipClicked}
            isTooltipClicked={isTooltipClicked}
          />
        )}
        {isAvvenimentoTemplate ? (
          <div
            className={classNames(styles.container, {
              [styles.fullWidth]: isAvvenimento,
            })}
          >
            {renderComponent()}
          </div>
        ) : (
          renderComponent()
        )}
      </>
    );
  };

  if (isAvvenimentoTemplate) {
    return <div>{renderElements()}</div>;
  }

  return <>{renderElements()}</>;
};
